import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import type { ExtractAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { useOverlay } from '@flipster/overlay';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css, cx } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { datadog } from '@flipster/utils/datadog';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment, { WalletEvent } from '@flipster/utils/segment';
import { MarginBonusEvent } from '@flipster/utils/segment/events/margin-bonus';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';
import { StakeEvent } from '@flipster/utils/segment/events/stake';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import sleep from '@flipster/utils/sleep';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { TradeUnexpectedError } from '../errors';
import { OrderEventTracker } from '../tracker';
import { submitBunja } from './bunja/action';
import { perpAccountBunja } from './bunja/base';
import { eventBunja } from './bunja/event';
import { formBunja } from './bunja/form';
import { displayPriceBunja } from './bunja/price';
import { stakedBalanceBunja } from './bunja/staked-balance';
import { Side, validationBunja } from './bunja/validation';
import { OrderNotPlacedDialog } from './modals/order-not-placed-dialog';
import { actions, col, row } from './styles';

const Actions = () => {
  const t = useTranslations();
  const store = useStore();
  const overlay = useOverlay();
  const { user } = useAuth();
  const { symbol } = useBunja(tradeSymbolBunja);
  const { recentOrderErrorsAtom, priceTypeAtom, sideAtom, checkValidationAll } = useBunja(formBunja);
  // 아래 atom들은 제출(클릭) 시점에만 필요한 값이라 구독하지 않고 store.get으로 읽는다
  // (realtime price/validation/position 갱신마다 버튼 영역이 리렌더되는 것을 방지)
  const { validationErrorsAtom } = useBunja(validationBunja);
  const { orderEventContextInfoAtom, onOrderSubmit, onOrderFailed } = useBunja(eventBunja);
  const { submitOrder } = useBunja(submitBunja);
  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const { isOneWayAtom } = useBunja(perpAccountBunja);
  const setSide = useSetAtom(sideAtom);
  const setRecentOrderErrors = useSetAtom(recentOrderErrorsAtom);

  const { kycInfo, isRequireKyc } = useKYC();
  const { isCookieLoggedIn } = useAuth();
  const isMarketPrice = useAtomValue(priceTypeAtom) === 'market';

  const storageKey = user && getFlipsterConfigsV1Key(user.userId, 'skipAdditionalOrderWarningInProfitBoost');
  const canSkip = (storageKey && (getLocalStorage(storageKey) as boolean)) || false;

  const { hasStakedBalanceAtom, canSkipStakedBalanceWarningAtom, setSkipStakedBalanceWarning } =
    useBunja(stakedBalanceBunja);

  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['submitOrder'],
    mutationFn: async () => {
      const orderEventContextInfo = store.get(orderEventContextInfoAtom);
      const validationErrors = store.get(validationErrorsAtom);
      await OrderEventTracker.instance.withContext(orderEventContextInfo, async (ctx) => {
        onOrderSubmit(ctx);
        if (Object.keys(validationErrors).length === 0) {
          const { openDialog } = (await submitOrder(ctx)) ?? {};
          // 다이얼로그(JSX)만 UI가 연다(토스트는 데이터 레이어가 직접 처리). 새 다이얼로그는 case 추가.
          switch (openDialog?.name) {
            case 'orderNotPlaced':
              segment.track(MarginBonusEvent.OrderNotPlacedAutoLeveragePopupView);
              overlay.open(({ isOpen, onClose, unmount }) => (
                <OrderNotPlacedDialog
                  isOpen={isOpen}
                  onClose={onClose}
                  onExit={unmount}
                  maxInitLeverageRestriction={openDialog.maxInitLeverageRestriction}
                />
              ));
              break;
          }
        } else {
          setRecentOrderErrors(validationErrors);
          const errors = Object.values(validationErrors);
          const toastError = errors.find((err) => err.type === 'toast');
          if (toastError?.message) {
            snackbar.error({ message: toastError.message });
            onOrderFailed(ctx, { type: 'validation', error: toastError.message });
            return;
          }
          const fieldError = errors.find((err) => err.type === 'field');
          if (fieldError?.message) {
            snackbar.error({ message: fieldError.message });
            onOrderFailed(ctx, { type: 'validation', error: fieldError.message });
            return;
          }
          const unknownError = errors.find((err) => err.type !== 'custom');
          if (unknownError) {
            snackbar.error({ message: t('Common.r250401.Unexpected_error_message_multiline') });
            onOrderFailed(ctx, { type: 'unknown', error: t('Common.r250401.Unexpected_error_message_multiline') });
            // oxlint-disable-next-line no-console
            console.error(unknownError); // 예상 외 에러가 있을 경우 디버깅하기위해서 에러를 추가
            datadog.addError(new TradeUnexpectedError(`${unknownError.type}: ${unknownError.message}`));
          }
        }
      });
    },
  });

  const showStakedBalanceWarning = (onNext: () => void) => {
    let isChecked = false;
    segment.track(StakeEvent.LaunchpoolStakedAssetTradingEligibilityBottomsheetView, {});
    alert.show({
      title: (
        <div className={hstack({ gap: 4 })}>
          <CautionCircleIcon size={24} className={css({ color: 'content.state.warning' })} />
          {t('Trade.r251020.Staked_assets_used_in_trading')}
        </div>
      ),
      message: (
        <div className={vstack({ gap: 16, alignItems: 'flex-start' })}>
          <div>{t('Trade.r251020.Your_assets_staked_in_Launchpool_reduced_info')}</div>
          <Checkbox
            onChange={(e) => {
              isChecked = e.target.checked;
            }}
            css={{ color: 'content.default.level-2' }}
          >
            {t('Common.r251020.Dont_show_again')}
          </Checkbox>
        </div>
      ),
      onConfirm: () => {
        if (isChecked) setSkipStakedBalanceWarning(true);
        segment.track(StakeEvent.LaunchpoolStakedAssetEligibilityConfirmClick, {});
        onNext();
      },
      confirmText: t('Trade.r250401.Got_it'),
      closeText: t('Common.r250401.Cancel'),
      withClose: true,
    });
  };
  const showProfitBoostWarning = async (
    currentPosition: NonNullable<ExtractAtomValue<typeof openPositionsAtom>>[string][0],
    onNext: () => void,
  ) => {
    let isChecked = false;
    alert.show({
      title: (
        <div className={hstack({ gap: 4 })}>
          <CautionCircleIcon size={24} className={css({ color: 'content.state.warning' })} />
          {t('Position_profit_boost.r250401.Add_position_warning_title')}
        </div>
      ),
      message: (
        <div className={vstack({ gap: 16, alignItems: 'flex-start' })}>
          <div>{t('Position_profit_boost.r250401.Add_position_warning_description')}</div>
          <Checkbox
            onChange={(e) => {
              isChecked = e.target.checked;
            }}
            css={{ color: 'content.default.level-2' }}
          >
            {t('Common.r250401.Dont_show_warning_again')}
          </Checkbox>
        </div>
      ),
      onConfirm: () => {
        segment.track(ProfitBoostEvent.ProfitBoostAddPositionConfirmClick, {
          position_id: currentPosition.positionId,
          event_location: 'order_form_page',
        });
        if (isChecked && storageKey) setLocalStorage(storageKey, true);
        onNext();
      },
      confirmText: t('Common.r250401.Confirm'),
      closeText: t('Common.r250401.Cancel'),
      withClose: true,
    });
  };

  const handleSubmit = async (side: Side) => {
    // 전체 error를 보기위해 errorTarget을 undefined로 설정
    checkValidationAll();

    const warnings: Array<(onNext: () => void) => void> = [];

    if (store.get(isOneWayAtom)) {
      const openPositions = store.get(openPositionsAtom);
      const currentPosition =
        openPositions?.[symbol] && Boolean(openPositions[symbol].length) ? openPositions[symbol][0] : undefined;
      const shouldShowProfitBoostWarning =
        currentPosition && currentPosition.boostExpiryTimestamp !== null && currentPosition.side === side && !canSkip;

      if (shouldShowProfitBoostWarning) {
        warnings.push((onNext) => showProfitBoostWarning(currentPosition, onNext));
      }
    }
    if (store.get(hasStakedBalanceAtom) && !store.get(canSkipStakedBalanceWarningAtom)) {
      warnings.push(showStakedBalanceWarning);
    }

    const executeWarnings = async (index: number) => {
      if (index >= warnings.length) {
        return mutateAsync();
      }
      await sleep(500);
      warnings[index](() => executeWarnings(index + 1));
    };

    if (warnings.length > 0) {
      return executeWarnings(0);
    }

    await mutateAsync();
  };

  if (!isCookieLoggedIn)
    return (
      <div className={cx(row, actions)}>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          as='a'
          href='/signin'
          label={t('Trade.r250401.Signup_or_login_to_trade_button')}
          onClick={() => segment.track(TradeEvent.OrderFormSignInClick, { symbol })}
          css={{ w: '100%', zIndex: 100 }}
        />
      </div>
    );

  if (isRequireKyc)
    return (
      <div className={cx(row, actions)}>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          as='a'
          href='/user/kyc'
          label={t('Trade.r250401.Verify_to_trade_button')}
          onClick={() => segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel })}
          css={{ w: '100%', zIndex: 100 }}
        />
      </div>
    );

  return (
    <div className={cx(row, actions)}>
      <div className={col}>
        <Button
          hierarchy='primary'
          color='buy'
          size='large'
          label={t('Trade.r250401.Buy_long_button')}
          subLabel={isMarketPrice ? <MarketPriceSubLabel side='Long' /> : undefined}
          onClick={async () => {
            setSide('Long');
            await handleSubmit('Long');
          }}
          css={{ w: '100%' }}
          isLoading={isPending}
        />
      </div>
      <div className={col}>
        <Button
          hierarchy='primary'
          color='sell'
          size='large'
          label={t('Trade.r250401.Sell_short_button')}
          subLabel={isMarketPrice ? <MarketPriceSubLabel side='Short' /> : undefined}
          onClick={async () => {
            setSide('Short');
            await handleSubmit('Short');
          }}
          css={{ w: '100%' }}
          isLoading={isPending}
        />
      </div>
    </div>
  );
};

/**
 * market 모드 버튼의 실시간 호가 라벨.
 * realtime ask/bid 구독을 이 컴포넌트로 격리해 tick당 리렌더 범위를 라벨로 한정한다
 * (버튼/폼 영역 전체가 매 tick 리렌더되는 것을 방지).
 */
const MarketPriceSubLabel = ({ side }: { side: Side }) => {
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision } = useAtomValue(symbolInfoWithDefaultAtom);
  const { throttledAskPriceAtom, throttledBidPriceAtom } = useBunja(displayPriceBunja);
  const price = useAtomValue(side === 'Long' ? throttledAskPriceAtom : throttledBidPriceAtom);
  return <Formatted.Number value={price} precision={pricePrecision} toFixed />;
};

export default Actions;
